import { Status } from '@/utils/constants';
import {
	createServiceMessage,
	getParameterByName,
	logger,
	logSending
} from './helpers';
import { ServiceData } from '@/types/generalTypes';
import { GameConfigs } from '@/types/gameConfig';
import { WSProps } from './SocketManager.interface';

const END_MESSAGE = '\r\n';

export class WS {
	WS_URL = '';
	reconnectTimeout = 0;
	pingInterval = 0;
	count = 0;
	msgBuffer = '';
	websocket: WebSocket | null;
	GameConfigs: Partial<GameConfigs> = {};

	errorHandler: WSProps['errorHandler'] = () => {};
	onCallError: WSProps['onCallError'] = () => {};
	receiveMessage: WSProps['receiveMessage'] = () => {};

	constructor({
		WS_URL,
		GameConfigs,
		receiveMessage,
		errorHandler,
		onCallError
	}: WSProps) {
		this.WS_URL = WS_URL;
		this.GameConfigs = GameConfigs;
		this.errorHandler = errorHandler;
		this.onCallError = onCallError;
		this.receiveMessage = receiveMessage;

		this.websocket = null;
		this.reconnectTimeout = 0;
		this.pingInterval = 0;
		this.count = 0;
		this.msgBuffer = '';
	}

	start() {
		logger.log(
			`%cStart websocket to ${this.WS_URL}`,
			'color:black; background: #0b646f; padding: 10px; border-radius: 12px;'
		);

		this.websocket = new WebSocket(this.WS_URL);

		if (!this.websocket) return this;

		this.websocket.onopen = () => this.onOpen();
		this.websocket.onmessage = (evt) => this.onReceiveMessage(evt);
		this.websocket.onclose = () => this.onClose();
		this.websocket.onerror = (evt) => this.onError(evt as ErrorEvent);

		return this;
	}

	onOpen() {
		logger.log('%cOPEN CONNECTION <3', 'color:white; padding: 10px;');

		this.setStatus(Status.CONNECTED);
		this.pingInterval = setInterval(this.pingIntervalFunc.bind(this), 5000);
	}

	onError(e: ErrorEvent) {
		logger.log({ e }, this.GameConfigs.errors, 'on error in websocket');
	}

	onClose() {
		this.disconnect();
	}

	onReceiveMessage(evt: MessageEvent) {
		try {
			const msg = evt.data;
			this.msgBuffer += msg;

			if (this.msgBuffer.indexOf(END_MESSAGE) !== -1) {
				const splitted: string[] = this.msgBuffer.split(END_MESSAGE);
				splitted.forEach((item) => {
					if (item.length > 0) {
						this.receiveMessage(JSON.parse(item) as ServiceData);
					}
				});

				this.msgBuffer = splitted[splitted.length - 1];
			}
		} catch (error) {
			this.onError({
				message: 'Custom call error Message - ' + error
			} as ErrorEvent);

			this.onCallError(true);
			logger.log(error);
		}
	}

	sendMessage(msg: string, callback?: (msg: string) => void) {
		const parsed = JSON.parse(msg);
		if (parsed.service !== 'ping') {
			this.count = 0;
		}

		logSending(msg);

		if (this.websocket?.readyState === WebSocket.OPEN) this.websocket.send(msg);

		callback?.(msg);
	}

	pingIntervalFunc() {
		const msg = createServiceMessage('ping');
		this.count++;

		if (this.GameConfigs.errors && this.GameConfigs.errors.code > 0) {
			clearInterval(this.pingInterval);
			this.disconnect();
		} else {
			this.sendMessage(msg);
		}

		if (this.count >= 60) {
			this.disconnect();
		}
	}

	disconnect() {
		clearInterval(this.pingInterval);
		clearTimeout(this.reconnectTimeout);
		this.setStatus(Status.DISCONNECTED);

		if (this.websocket) {
			this.websocket.onerror = null;
			this.websocket.onopen = null;
			this.websocket.onmessage = null;
			this.websocket.onclose = null;
			this.websocket.close();
		}

		if (this.GameConfigs.errors && this.GameConfigs.errors.code > 0) {
			this.errorHandler(this.GameConfigs.errors, this.GameConfigs.App);
		} else {
			this.errorHandler(
				{
					code: 1000,
					message: 'connection lost',
					status: 'connetcion lost'
				},
				this.GameConfigs.App
			);
		}
	}

	setStatus(status: Status) {
		switch (status) {
			case Status.CONNECTED:
				logger.log(
					'%cCONNECTED!',
					'color:black; background: green; border-radius: 4px; padding: 10px; font-size: 16px'
				);
				this.sendLoginMessage(status);
				break;
			case Status.DISCONNECTED:
				logger.log(
					'%cDISCONNECTED :(',
					'color:white; background: red; padding: 10px; font-size: 16px'
				);
				break;

			default:
				break;
		}
	}

	sendLoginMessage(status?: Status) {
		const session = getParameterByName('session');
		if (typeof session === 'string') {
			const loginMsg = createServiceMessage('auth', session);
			this.sendMessage(loginMsg);
		} else {
			console.error(
				`%cnot logged in ${status};`,
				'color: white; background: red;'
			);
		}
	}
}
